import { Box, Typography } from '@mui/material';
import { isCancelledSummaryCard } from '../utils/listSummaryUtils';
import { showCancelledRecordsPopup } from '../utils/PopupUtils';

const cardSx = {
    flex: '0 0 auto',
    minWidth: 128,
    borderRadius: '12px',
    px: 1.75,
    py: 1.1,
    color: '#fff',
    whiteSpace: 'nowrap',
    textAlign: 'right',
};

const labelSx = {
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    opacity: 0.9,
    lineHeight: 1.2,
};

const valueSx = {
    fontSize: '1.35rem',
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    mt: 0.25,
};

const subValueSx = {
    fontSize: '0.75rem',
    fontWeight: 600,
    opacity: 0.95,
    lineHeight: 1.2,
    mt: 0.2,
};

export function GradientCard({ card, onClick, compact = false }) {
    return (
        <Box
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick(event);
                }
            } : undefined}
            sx={{
                ...cardSx,
                ...(compact ? { minWidth: 108, px: 1.2, py: 0.65 } : {}),
                background: card.gradient,
                boxShadow: card.shadow,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': onClick ? {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.18)',
                } : undefined,
            }}
        >
            <Typography sx={{ ...labelSx, ...(compact ? { fontSize: '0.6rem' } : {}) }}>{card.label}</Typography>
            <Typography sx={{ ...valueSx, ...(compact ? { fontSize: '1.05rem', mt: 0.1 } : {}) }}>{card.value}</Typography>
            {card.subValue ? (
                <Typography sx={{ ...subValueSx, ...(compact ? { fontSize: '0.65rem' } : {}) }}>
                    {card.subValue}
                </Typography>
            ) : null}
        </Box>
    );
}

/**
 * @param {object} [cancelledPopup] When set, clicking a Cancelled* card opens the details popup.
 *   { title, columns, getRows, mapRow?, footerRowBuilder?, emptyMessage?, width? }
 */
function ListPageGradientSummary({
    highlightCard,
    cards = [],
    onHighlightClick,
    onCardClick,
    cancelledPopup,
}) {
    if (!highlightCard && !cards.length) {
        return null;
    }

    const handleCardClick = (card) => {
        if (onCardClick) {
            onCardClick(card);
            return;
        }
        if (!cancelledPopup || !isCancelledSummaryCard(card)) {
            return;
        }
        const sourceRows = typeof cancelledPopup.getRows === 'function'
            ? (cancelledPopup.getRows() || [])
            : (cancelledPopup.rows || []);
        const displayRows = cancelledPopup.mapRow
            ? sourceRows.map(cancelledPopup.mapRow)
            : sourceRows;
        const footerRow = typeof cancelledPopup.footerRowBuilder === 'function'
            ? cancelledPopup.footerRowBuilder(sourceRows, displayRows)
            : cancelledPopup.footerRow || null;
        showCancelledRecordsPopup({
            title: cancelledPopup.title || `Cancelled (${sourceRows.length})`,
            columns: cancelledPopup.columns || [],
            rows: displayRows,
            footerRow,
            emptyTitle: cancelledPopup.emptyTitle || 'Cancelled Records',
            emptyMessage: cancelledPopup.emptyMessage || 'No cancelled records in the current list.',
            width: cancelledPopup.width || 980,
        });
    };

    return (
        <Box
            className="container"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                mb: 1,
                py: 0.5,
            }}
        >
            {highlightCard ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0, flexWrap: 'wrap' }}>
                    <GradientCard card={highlightCard} onClick={onHighlightClick} />
                </Box>
            ) : null}

            {cards.length > 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        gap: 1.25,
                        flex: 1,
                        justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                        minWidth: 0,
                        overflowX: 'auto',
                    }}
                >
                    {cards.map((card) => (
                        <GradientCard
                            key={card.label}
                            card={card}
                            onClick={
                                onCardClick || (cancelledPopup && isCancelledSummaryCard(card))
                                    ? () => handleCardClick(card)
                                    : undefined
                            }
                        />
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}

export default ListPageGradientSummary;
